import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const ARTICLE_URL = "https://www.hey-lets-travel.com/community/chris-stussy-uss-pacha-ibiza-2026";
const IMAGE_URL = "https://www.hey-lets-travel.com/assets/community/931034ec-7374-496d-9754-cc1d8d26c87e.png";
const TICKET_URL = "https://www.ibiza-spotlight.com/night/promoters/chris-stussy-pacha";
const CONCIERGE_URL = "/concierge";
const EVENTS_URL = "/events";

const FAQS = [
  {
    question: "When is Chris Stussy presents USS at Pacha Ibiza?",
    answer: "Chris Stussy presents USS at Pacha Ibiza is listed for Tuesday 26 May 2026, with another Pacha date on Tuesday 2 June 2026.",
  },
  {
    question: "Where is Chris Stussy playing in Ibiza?",
    answer: "The event takes place at Pacha Ibiza, one of the island's most recognised clubs.",
  },
  {
    question: "What music does Chris Stussy play?",
    answer: "Chris Stussy is known for deep house, minimal house, garage-influenced grooves and rolling underground club music.",
  },
  {
    question: "What is USS?",
    answer: "USS is Chris Stussy's own event concept, built around a focused, music-first club experience.",
  },
  {
    question: "Can HLT help with hotels, tables and concierge for Pacha Ibiza?",
    answer: "Yes. HLT can help with Ibiza hotels, villas, transfers, VIP tables, after-party planning and full concierge support around Pacha Ibiza events.",
  },
];

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "Chris Stussy Presents USS at Pacha Ibiza 2026: The Music-First Night Built for Serious House Fans",
  description:
    "Chris Stussy brings USS to Pacha Ibiza in 2026. Read the HLT guide to his rise, the USS concept, tickets, hotels and Ibiza concierge support.",
  image: [IMAGE_URL],
  mainEntityOfPage: ARTICLE_URL,
  datePublished: "2026-05-26",
  dateModified: "2026-05-26",
  author: { "@type": "Organization", name: "Hey Let's Travel" },
  publisher: { "@type": "Organization", name: "Hey Let's Travel" },
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: { "@type": "Answer", text: item.answer },
  })),
};

function TicketCta({ children = "View Chris Stussy Pacha Tickets" }) {
  return (
    <a href={TICKET_URL} target="_blank" rel="noopener noreferrer" className="btn-primary">
      {children} -&gt;
    </a>
  );
}

function ConciergeCta({ children = "Plan Chris Stussy at Pacha with HLT" }) {
  return (
    <Link to={CONCIERGE_URL} className="btn-secondary">
      {children} -&gt;
    </Link>
  );
}

function ArticleSection({ title, children }) {
  return (
    <section className="space-y-4 border-t border-white/10 pt-8">
      <h2 className="font-display text-4xl leading-none text-white md:text-5xl">{title}</h2>
      <div className="space-y-4 text-base leading-8 text-slate-300">{children}</div>
    </section>
  );
}

export default function ChrisStussyPachaArticle() {
  return (
    <>
      <Helmet>
        <title>Chris Stussy Pacha Ibiza 2026 Tickets | USS Ibiza Guide</title>
        <meta
          name="description"
          content="Chris Stussy brings USS to Pacha Ibiza in 2026. Read the HLT guide to his rise, the USS concept, tickets, hotels and Ibiza concierge support."
        />
        <meta
          name="keywords"
          content="Chris Stussy Ibiza 2026, Chris Stussy Pacha tickets, USS Pacha Ibiza, Chris Stussy USS, Pacha Ibiza house music, Ibiza house music events 2026, Ibiza deep house events, Ibiza minimal house, HLT Ibiza concierge, Ibiza VIP tables, Ibiza hotel and ticket package"
        />
        <link rel="canonical" href={ARTICLE_URL} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Chris Stussy Pacha Ibiza 2026 Tickets | USS Ibiza Guide" />
        <meta property="og:description" content="Plan Chris Stussy presents USS at Pacha Ibiza with HLT tickets, hotels, VIP support and concierge planning." />
        <meta property="og:url" content={ARTICLE_URL} />
        <meta property="og:image" content={IMAGE_URL} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Chris Stussy Pacha Ibiza 2026 Tickets" />
        <meta name="twitter:description" content="Chris Stussy brings USS to Pacha Ibiza. Build the night into a full Ibiza trip with HLT." />
        <meta name="twitter:image" content={IMAGE_URL} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <article className="page space-y-10">
        <header className="grid gap-6 lg:grid-cols-[minmax(0,1fr),30rem] lg:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-500">Ibiza 2026 guide</p>
            <h1 className="mt-4 font-display text-5xl leading-none text-white md:text-7xl">
              Chris Stussy Presents USS at Pacha Ibiza 2026
            </h1>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-slate-300">
              A music-first Pacha Ibiza night built around deep house, minimal groove, garage influence and one of the strongest modern underground followings in dance music.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <TicketCta />
              <ConciergeCta children="Build the Ibiza trip with HLT" />
            </div>
          </div>
          <aside className="border border-brand-500/25 bg-[#111111] p-5">
            <p className="text-xs uppercase tracking-[0.22em] text-brand-500">Event details</p>
            <dl className="mt-4 grid gap-3 text-sm text-slate-300">
              <div><dt className="text-white">Event</dt><dd>Chris Stussy presents USS</dd></div>
              <div><dt className="text-white">Venue</dt><dd>Pacha Ibiza</dd></div>
              <div><dt className="text-white">Date</dt><dd>Tuesday 26 May 2026</dd></div>
              <div><dt className="text-white">Additional date</dt><dd>Tuesday 2 June 2026</dd></div>
              <div><dt className="text-white">Time</dt><dd>From 23:00 until late</dd></div>
              <div><dt className="text-white">Sound</dt><dd>Deep house, minimal house, garage-influenced grooves and rolling underground club music</dd></div>
            </dl>
          </aside>
        </header>

        <a href={TICKET_URL} target="_blank" rel="noopener noreferrer" className="group block overflow-hidden border border-brand-500/25 bg-black shadow-[0_24px_90px_rgba(0,0,0,0.55)] transition hover:border-brand-500/70">
          <img src="/assets/community/931034ec-7374-496d-9754-cc1d8d26c87e.png" alt="Chris Stussy presents USS at Pacha Ibiza event poster" className="block h-auto w-full transition duration-500 group-hover:scale-[1.01]" />
        </a>

        <div className="mx-auto max-w-4xl space-y-10">
          <section className="space-y-4 text-base leading-8 text-slate-300">
            <p>
              Chris Stussy brings USS to Pacha Ibiza in 2026 for a focused, music-first night on one of the island's most famous dancefloors. For HLT, this is exactly the type of event Ibiza travel should be built around: a clear artist identity, a crowd that knows the sound and a night strong enough to shape the whole trip.
            </p>
            <p>
              This is not a generic club listing. It is a moment for people who follow house music properly: deep rhythm, stripped percussion, garage weight, rolling basslines and the kind of hypnotic groove that has pushed Chris Stussy from underground favourite to one of the most watched names in modern house.
            </p>
            <div className="flex flex-wrap gap-3">
              <TicketCta children="View Chris Stussy Pacha Tickets" />
              <ConciergeCta children="Ask HLT to build the full Ibiza plan" />
            </div>
          </section>

          <ArticleSection title="Why Chris Stussy matters right now">
            <p>
              Over the last five years, Chris Stussy has become one of the clearest examples of how underground house can grow without losing its identity. His records, DJ sets and label work all sit in the same lane: detailed, rolling, groove-led club music that rewards people who listen closely but still works on big dancefloors.
            </p>
            <p>
              The Dutch DJ and producer has released across respected labels and built his own world through Up The Stuss, the label he launched in 2020. That label has become a reference point for a new wave of house music: minimal, deep, warm, precise and built for dancers rather than algorithms.
            </p>
            <p>
              Tracks such as Desire, Pumpin', All Night Long, Won't Stop (Don't) and Breather helped widen his reach while keeping him connected to the underground sound that first made people pay attention. The result is rare: a DJ who can headline serious club rooms, festival stages and Ibiza parties while still feeling like a selector first.
            </p>
          </ArticleSection>

          <ArticleSection title="What USS brings to Pacha Ibiza">
            <p>
              USS is Chris Stussy's own party concept, and the Ibiza edition follows the same idea: music at the centre, fewer distractions and a room built around connection. Pacha gives that concept a very specific stage.
            </p>
            <p>
              Pacha is polished, historic and globally recognised, but the right night can still feel intimate once the room locks in. That is why Chris Stussy at Pacha makes sense. It brings a refined underground sound into a venue that can make a focused house night feel huge.
            </p>
            <p>
              For travellers, that matters. A strong Ibiza night is not just about who is playing. It is about the venue, the crowd, the timing, the surrounding plans and the feeling that the event has a reason to exist. USS at Pacha has that shape.
            </p>
          </ArticleSection>

          <ArticleSection title="Why this is an HLT Ibiza trip event">
            <p>
              Ibiza trips are increasingly planned around specific artists and lineups. People are no longer waiting until they land to decide where to go. They know the sound they want, they know the venues they trust and they want the rest of the trip built around that centrepiece.
            </p>
            <p>
              Chris Stussy at Pacha is ideal for that kind of planning. It works as a premium night for couples, a centrepiece for a group trip, a high-quality stop on a longer Ibiza week or a reason to fly in for a focused midweek escape.
            </p>
            <p>
              Through HLT, that night can become more than a ticket. It can become the anchor for hotels, villas, transfers, VIP table enquiries, restaurant plans, beach club days, after-party access and a proper concierge-led Ibiza itinerary.
            </p>
          </ArticleSection>

          <ArticleSection title="Plan Chris Stussy at Pacha with HLT">
            <p>If you are building a trip around Chris Stussy at Pacha, HLT can help you shape the rest of the experience around the night.</p>
            <ul className="list-disc space-y-2 pl-6 text-slate-200">
              <li>Chris Stussy Pacha Ibiza ticket access</li>
              <li>Hotels close to Ibiza Town, Talamanca, Playa d'en Bossa and key nightlife routes</li>
              <li>Group villas for friends who want privacy and pool days</li>
              <li>Airport transfers and club transport</li>
              <li>VIP table enquiries and premium nightlife support</li>
              <li>After-party, villa party and private group planning</li>
              <li>Full Ibiza packages combining tickets, hotels, transfers and concierge help</li>
            </ul>
            <div className="flex flex-wrap gap-3">
              <TicketCta children="View ticket options" />
              <ConciergeCta children="Request an Ibiza concierge package" />
              <Link to={EVENTS_URL} className="btn-secondary">Browse more Ibiza events</Link>
            </div>
          </ArticleSection>

          <ArticleSection title="Where to stay for Pacha Ibiza">
            <p>
              Pacha is close to Ibiza Town and Talamanca, which makes those two areas especially useful if this event is the centre of your trip. Ibiza Town gives you restaurants, marina energy, pre-drinks and easy taxi access. Talamanca gives you a more polished hotel stay while keeping the club within reach.
            </p>
            <p>
              Playa d'en Bossa can also work if you want to combine Pacha with Hi Ibiza, Ushuaia and beach club plans. San Antonio can be more budget-friendly, but you need to plan transport properly. For groups, a villa can make the whole trip feel more premium, especially if you want pool days and private pre-party time.
            </p>
            <ConciergeCta children="Ask HLT where to stay for Pacha" />
          </ArticleSection>

          <ArticleSection title="Who should go?">
            <p>
              This is the right Ibiza night if your group is into Chris Stussy, Up The Stuss, PIV, Eastenderz, Fuse London, deep house, minimal house, UK garage-influenced grooves and rolling underground club music.
            </p>
            <p>
              It is also a strong choice if you want a premium Ibiza night that still feels music-led. Pacha gives the polish. Chris Stussy brings the groove. USS gives the night its identity.
            </p>
          </ArticleSection>

          <ArticleSection title="Final word">
            <p>
              Chris Stussy presents USS at Pacha Ibiza is exactly the kind of event HLT wants to put in front of house music travellers. It has a clear sound, a serious artist, a globally recognised venue and enough weight to build a trip around.
            </p>
            <p>
              Book the ticket, shape the hotel around the night and let the rest of the trip feel intentional. Ibiza is better when the music leads.
            </p>
            <div className="flex flex-wrap gap-3">
              <TicketCta children="View Chris Stussy Pacha tickets" />
              <ConciergeCta children="Build the trip with HLT" />
            </div>
          </ArticleSection>

          <section className="border-t border-white/10 pt-8">
            <h2 className="font-display text-4xl leading-none text-white md:text-5xl">FAQ</h2>
            <div className="mt-5 grid gap-3">
              {FAQS.map((item) => (
                <details key={item.question} className="border border-white/10 bg-[#111111] p-4">
                  <summary className="cursor-pointer font-semibold text-white">{item.question}</summary>
                  <p className="mt-3 leading-7 text-slate-300">{item.answer}</p>
                </details>
              ))}
            </div>
          </section>
        </div>
      </article>
    </>
  );
}
